import { format, parse } from "date-fns";
import { EMPLOYMENT_TYPE, type EmploymentType } from "./constants";
import { toIsoDate } from "./date";
import {
	employmentTypeToTherapistType,
	type TherapistType,
	therapistTypeToEmploymentType,
} from "./employment-type";
import type { SlotSelection } from "./types";

/**
 * Compose the picker's `{ date: "YYYY-MM-DD", start_time: "HH:mm" }` into the
 * single `Date` the appointment form persists as `appointmentDateTime` (the
 * backend contract has no separate start/end — time is baked into the Date).
 */
export function slotSelectionToDateTime(selection: SlotSelection): Date {
	return parse(
		`${selection.date} ${selection.start_time}`,
		"yyyy-MM-dd HH:mm",
		new Date(),
	);
}

/** The therapist object the form's `therapist` field expects, incl. employment type. */
export function slotSelectionToTherapist(selection: SlotSelection): {
	id: string;
	name: string;
	employmentType: EmploymentType;
} {
	return {
		id: selection.therapistId,
		name: selection.therapistName,
		employmentType: therapistTypeToEmploymentType(selection.therapistType),
	};
}

function isEmploymentType(value: string): value is EmploymentType {
	return value === EMPLOYMENT_TYPE.KARPIS || value === EMPLOYMENT_TYPE.FLAT;
}

/**
 * Rebuild the picker's `value` from the two fields the form already persists —
 * the inverse of {@link slotSelectionToTherapist} + {@link slotSelectionToDateTime}.
 *
 * `end_time` cannot be recovered from the fields (the backend contract stores
 * only a start) and is not needed to highlight a committed slot, so it is "".
 * An unrecognised employment type falls back to "internal": passing it straight
 * to `employmentTypeToTherapistType` would silently map anything non-KARPIS to
 * "external".
 */
export function toSlotSelection(
	therapist:
		| {
				id?: string | number | null;
				name?: string | null;
				employmentType?: string | null;
		  }
		| null
		| undefined,
	dateTime: Date | string | null | undefined,
): SlotSelection | null {
	if (!therapist?.id || !dateTime) return null;

	const date = dateTime instanceof Date ? dateTime : new Date(dateTime);
	if (Number.isNaN(date.getTime())) return null;

	const employmentType = therapist.employmentType;
	const therapistType: TherapistType =
		employmentType && isEmploymentType(employmentType)
			? employmentTypeToTherapistType(employmentType)
			: "internal";

	return {
		therapistId: String(therapist.id),
		therapistName: therapist.name ?? "",
		therapistType,
		date: toIsoDate(date),
		start_time: format(date, "HH:mm"),
		end_time: "",
	};
}
